import { NavRoute } from './types';
import { useReferralReward } from '../../../../helpers/hooks/useReferralReward';

type FcBubble = { left: string; bottom: string; delay: string; duration?: string; size?: number };

const FC_BUBBLES: Record<string, FcBubble[]> = {
  blue: [
    { left: '18%', bottom: '10px', delay: '0s' },
    { left: '45%', bottom: '8px', delay: '2.2s', duration: '8s', size: 4 },
    { left: '72%', bottom: '12px', delay: '4.1s', duration: '6.5s' },
  ],
  green: [
    { left: '22%', bottom: '10px', delay: '.8s', duration: '8s' },
    { left: '55%', bottom: '8px', delay: '3.0s', size: 4 },
    { left: '78%', bottom: '14px', delay: '5.2s', duration: '7s' },
  ],
  ice: [
    { left: '15%', bottom: '10px', delay: '1.4s', duration: '7.5s' },
    { left: '50%', bottom: '8px', delay: '3.8s', size: 4 },
    { left: '80%', bottom: '12px', delay: '0.5s', duration: '9s' },
  ],
  gold: [
    { left: '20%', bottom: '10px', delay: '0.3s', duration: '8.5s' },
    { left: '48%', bottom: '8px', delay: '2.8s', size: 4 },
    { left: '75%', bottom: '12px', delay: '5.5s', duration: '7s' },
  ],
};

const renderBubbles = (variant: keyof typeof FC_BUBBLES) =>
  FC_BUBBLES[variant].map((b, i) => (
    <span
      key={i}
      className='fc-bubble'
      style={{
        left: b.left,
        bottom: b.bottom,
        animationDelay: b.delay,
        animationDuration: b.duration,
        width: b.size ?? 5,
        height: b.size ?? 5,
      }}
      aria-hidden='true'
    />
  ));

export const FeatureHighlights = ({
  onNav,
  onRefer,
}: {
  onNav: (route: NavRoute) => void;
  onRefer: () => void;
}) => {
  const { summaryText } = useReferralReward();

  const referBody = summaryText
    ? `${summaryText} per member who signs up and pays rent. `
    : 'Earn rewards for every member who signs up and pays rent. ';

  return (
    <div>
      <div className='sec-hdr' style={{ marginBottom: 18, alignItems: 'center' }}>
        <div className='sec-hdr-bar'>
          <div className='sec-hdr-accent' aria-hidden='true' />
          <div className='sec-title'>Make your rent work harder</div>
        </div>
      </div>
      <div className='fc-grid'>
        <div className='fc fc-blue' role='presentation'>
          {renderBubbles('blue')}
          <div>
            <div className='fc-hdr'>
              <div className='fc-icon-box'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#7eb5ff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <polygon points='12 2 14.9 8.5 22 9.3 16.6 14.1 18.2 21 12 17.3 5.8 21 7.4 14.1 2 9.3 9.1 8.5' />
                </svg>
              </div>
              <div className='fc-title'>Refer a member</div>
            </div>
            <div className='fc-body'>
              {referBody}
              <b>Stack referral bonuses monthly.</b>
            </div>
          </div>
          <div className='fc-chips'>
            <button type='button' className='fc-chip' onClick={onRefer}>
              Invite members
            </button>
          </div>
        </div>

        <div className='fc fc-green' role='presentation'>
          {renderBubbles('green')}
          <div>
            <div className='fc-hdr'>
              <div className='fc-icon-box'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#4ade80'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <polyline points='22 12 18 12 15 21 9 3 6 12 2 12' />
                </svg>
              </div>
              <div className='fc-title'>Build credit with rent</div>
            </div>
            <div className='fc-body'>
              Every on-time payment reported to Equifax. <b>Free. Every month.</b>
            </div>
          </div>
          <div className='fc-chips'>
            <button type='button' className='fc-chip' onClick={() => onNav('reporting')}>
              Activate reporting
            </button>
          </div>
        </div>

        <div className='fc fc-ice' role='presentation'>
          {renderBubbles('ice')}
          <div>
            <div className='fc-hdr'>
              <div className='fc-icon-box'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#b8d4ff'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z' />
                </svg>
              </div>
              <div className='fc-title'>Earn tickets</div>
            </div>
            <div className='fc-body'>
              This month&apos;s rent drop is live. Stack tickets before the drop closes. One per
              month. Yours could be next.
            </div>
          </div>
          <div className='fc-chips'>
            <button type='button' className='fc-chip' onClick={() => onNav('rewards')}>
              Start earning
            </button>
          </div>
        </div>

        <div className='fc fc-gold' role='presentation'>
          {renderBubbles('gold')}
          <div>
            <div className='fc-hdr'>
              <div className='fc-icon-box'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#f5a623'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <rect x='1' y='4' width='22' height='16' rx='2' ry='2' />
                  <line x1='1' y1='10' x2='23' y2='10' />
                </svg>
              </div>
              <div className='fc-title'>Save on rent</div>
            </div>
            <div className='fc-body'>
              Redeem your points toward rent or shop gift cards from 115+ brands including
              Starbucks, Apple, Netflix, and more.
            </div>
          </div>
          <div className='fc-chips'>
            <button type='button' className='fc-chip' onClick={() => onNav('wallet')}>
              Open rewards hub
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
